import { useCallback, useEffect, useRef, useState } from 'react';
import './AdminAnnouncement.scss';
import images from '../../assets/images';
import TabsContentHeader from '../../components/AdminDashboardTabsContentHeader/TabsContentHeader';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Menu, Tooltip } from '@mui/material';
import ToasterWithMessage from '../../components/toaster';
import ConfirmationPopup from '../../components/ConfirmationPopup/ConfirmationPopup';
import { debounce } from 'lodash';
import Loader from '../../components/Loader/Loader';
import { getMenuItemsForEditShare, getScreenDimensions } from '../../utils/helper';
import { DELETE_ANNOUNCEMENT, GET_ALL_ANNOUNCEMENTS, SHARE_TO_ALL_ANNOUNCEMENT } from './api/AdminAnnouncementQuery';
import AdminAnnouncementForm from './Components/AdminAnnouncementForm';
import AnnouncementViewer from './Components/AnnouncementViewer';
import MaterialActionsOverlay from '../../components/ActionsOverlay/MaterialActionsOverlay';
import { useAppSelector } from '../../redux/app/hooks';
import { SCHOOL_ADMIN, STUDENT_ROLE, TEACHER_ROLE } from '../../utils/constants';
import { READ_NOTIFICATIONS } from '../AdminDashboard/api/AdminDashboardQuery';
import { setRefetchNotification } from '../../redux/slices/authSlice';
import { useDispatch } from 'react-redux';
import MobileScreenHeader from '../../components/MobileScreenHeader/MobileScreenHeader';

export interface announcementObjectType {
        aId:string;
        aTitle:string;
        aOriginalTitle: string;
        aMessage:string;
        aUrl:string;
        saId:string;
        updatedAt:string;
        isShare: boolean;
        feedsByAId: {
            nodes: [
                {
                    fId: string;
                }
            ]
        }
}
interface announcementListType {
    allAnnouncements: {
        nodes: announcementObjectType[];
        totalCount: number;
    }
}

const AdminAnnouncement = () => {
    const { MoreIcon, AnnouncementIcon, DeleteGif, ShareGif, AnnouncementImage } = images;
    const [announcementsList, setAllAnnouncementsList] = useState<announcementListType>();
    const [displayForm, setDisplayForm] = useState<boolean>(false);
    const [openOverlay, setOpenOverlay] = useState<boolean>(false);
    const [actionData, setActionData] = useState<announcementObjectType>();
    const [displayDetailedView,setDisplayDetailedView] = useState<boolean>(false);
    const [singleAnnouncementDetail, setSingleAnnouncementDetail] = useState<announcementObjectType>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [refetchData, setRefetchData] = useState<boolean>(false);
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const [clickAction, setClickAction] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const { success, error } = ToasterWithMessage();
    const announcementTitleRefs = useRef<Array<HTMLLabelElement | null>>([]);
    const [isOverflow, setIsOverflow] = useState<boolean[]>([]);
    const baseUrl = process.env.REACT_APP_FRONTEND_URL;
    const [action, setAction] = useState<string>('');
    const role = useAppSelector((state) => state.auth.role);
    const profile = useAppSelector((state) => state.auth.adminProfile);
    const dispatch = useDispatch();
    const profileImage = useAppSelector((state) => state?.auth?.adminProfileImage);
    const StudentName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;

    const [getAllAnnouncements, { loading }] = useLazyQuery(GET_ALL_ANNOUNCEMENTS, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            setAllAnnouncementsList(data);
            setRefetchData(false);
        }
    });
    
    const [deleteAnnouncement] = useMutation(DELETE_ANNOUNCEMENT, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
          success("Announcement deleted successfully")
          setClickAction('');
          setRefetchData(true);
        },
        onError: (err) => {
          error(err?.message);
        }
      });

      const [shareToAllAnnouncement] = useMutation(SHARE_TO_ALL_ANNOUNCEMENT, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
          if(action === 'undoShare'){
            success("Unshared successfully")
          }else {
            success("Announcement shared successfully")
          }
          setClickAction('');
          setRefetchData(true);
        },
        onError: (err) => {
          error(err?.message);
        }
      });

    const [readNotification] = useMutation(READ_NOTIFICATIONS, {
        fetchPolicy: "network-only",
        onCompleted: () => {
            dispatch(setRefetchNotification({ refetchNotification: true }));
        },
        onError: (err) => {
            console.log("Error", err?.message);
        }
    })

    useEffect(() => {
        getAllAnnouncements({
            variables: {
                searchInput: '%%',
                limit:null,
                offset:null
            }
        });
    }, []);

    useEffect(() => {
        if (displayForm === false) {
            getAllAnnouncements({
                variables: {
                    searchInput: `%${searchValue.trim()}%`,
                    limit:null,
                    offset:null
                }
            });
        }
    }, [displayForm]);

    useEffect(() => {
        if (refetchData === true) {
            getAllAnnouncements({
                variables: {
                    searchInput: `%${searchValue.trim()}%`,
                    limit:null,
                    offset:null
                }
            });
        }
    }, [refetchData]);

    //To search classroom
    const handleSearchDebounce = useCallback(
        debounce((value) => {
            setSearchValue(value);
            getAllAnnouncements({
                variables: {
                    searchInput: `%${value.trim()}%`,
                    limit:null,
                    offset:null
                }
            });
        }, 500),
        [getAllAnnouncements]
    );

    //To open actions options
    const handleActions = (event: React.MouseEvent<HTMLElement>, data: announcementObjectType) => {
        event.stopPropagation(); // Stop propagation to prevent triggering parent click
        setAnchorEl(event.currentTarget);
        setActionData(data);
        setSingleAnnouncementDetail(data);
        setOpenOverlay(true);
    };

    //To close actions option
    const handleCloseActions = () => {
        setOpenOverlay(false);
    };


    const handleClickCard = (data: announcementObjectType) => {
        if (openOverlay === false) {
           setSingleAnnouncementDetail(data);
           setDisplayDetailedView(true);
           if (data?.feedsByAId?.nodes[0]?.fId && profile?.getCurrentUser?.nodes[0]?.uId) {
            readNotification({
                variables: {
                    fId: data?.feedsByAId?.nodes[0]?.fId,
                    uId: profile?.getCurrentUser?.nodes[0]?.uId
                }
               })
           }
        }
    };

    //To format classroom total count
    const formatAnnouncementCount = (count: number) => {
        if (count === 0) {
            return "No Announcement";
        } else if (count === 1) {
            return "1 Announcement";
        } else {
            return `${count} Announcements`;
        }
    };
    
    //To check the announcement title is overflow
    const checkOverflow = (index: number) => {
        if (announcementTitleRefs.current[index]) {
            const overflow = announcementTitleRefs.current[index]!.scrollWidth > announcementTitleRefs.current[index]!.clientWidth;
            setIsOverflow(prev => {
                const newOverflow = [...prev];
                newOverflow[index] = overflow;
                return newOverflow;
            });
        }
    };

    //To check overflow in resize 
    useEffect(() => {
        const handleResize = () => {
            announcementsList?.allAnnouncements?.nodes?.forEach((_, index) => {
                checkOverflow(index);
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [announcementsList]);

      //To check overflow in resize 
      useEffect(() => {
        if(announcementsList?.allAnnouncements?.nodes){
            announcementsList?.allAnnouncements?.nodes?.forEach((_, index) => {
                checkOverflow(index);
            });
        }

        const handleResize = () => {
            announcementsList?.allAnnouncements?.nodes?.forEach((_, index) => {
                checkOverflow(index);
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [announcementsList]);

    //To done actions in materials
    const handleMaterialActions = (data: announcementObjectType, action: string) => {
        setSingleAnnouncementDetail(data);
        if (action === "edit") {
            setDisplayForm(true);
            setClickAction("edit");
        } else if (action === "delete") {
            // deleteAnnouncement({
            //     variables: {
            //         aId: data?.aId
            //       }
            // })
        }
    };

    const undoShareAnnouncement = () => {
        shareToAllAnnouncement({
            variables: {
                isShare: false,
                aId: actionData?.aId
            }
        })
      }

    const menuItems = handleMaterialActions ? getMenuItemsForEditShare(handleCloseActions, handleMaterialActions, setOpenConfirmationPopup, setAction, actionData, undoShareAnnouncement):[];

     // click yes button in confirmation popup
  const onYesButtonClick = () => {
    setOpenConfirmationPopup(false);
    if(action === 'delete'){
        deleteAnnouncement({
            variables: {
              aId: actionData?.aId
            }
          });
    }else if(action === 'shareAll'){
        shareToAllAnnouncement({
            variables: {
                isShare: true,
                aId: actionData?.aId
            }
        })
    }else if(action === 'undoShare'){
        shareToAllAnnouncement({
            variables: {
                isShare: false,
                aId: actionData?.aId
            }
        })
    }
    
  }

  // click no button in confirmation popup
  const onNoButtonClick = () => {
    setOpenConfirmationPopup(false);
  }

    return (
        <>
            {displayDetailedView ? 
                    <>
                      <AnnouncementViewer
                       displayDetailedView={displayDetailedView}
                       setDisplayDetailedView={setDisplayDetailedView}
                       singleAnnouncementDetail={singleAnnouncementDetail}
                      />
                    </>
                   : displayForm ?
                    <>
                        <AdminAnnouncementForm
                            setDisplayForm={setDisplayForm}
                            displayForm={displayForm}
                            setClickAction={setClickAction}
                            clickAction={clickAction}
                            singleAnnouncementDetail={singleAnnouncementDetail}
                        />
                    </>
                    :
                    <div className={role === SCHOOL_ADMIN ?'AdminAnnouncement':'TeaacherStudentAnnouncement'}>
                        {getScreenDimensions()?.isMobileScreen && (
                            <MobileScreenHeader
                                profileImage={profileImage}
                                role={role}
                                studentName={StudentName}
                                profile={profile}
                            />
                        )}
                       {
                        (role === STUDENT_ROLE || role === TEACHER_ROLE) ?
                        <div className='announcementHeader'>
                            <div className='announcementHeaderTitle'>Announcement</div>
                            <span className='announcementHeaderDescription'>News from administration.</span>
                        </div>
                        :
                        <TabsContentHeader
                            title={"All Messages"}
                            subtitle={formatAnnouncementCount(announcementsList?.allAnnouncements?.totalCount || 0)}
                            addLabel={"Add Annoucement"}
                            setClickAction={setClickAction}
                            setDisplayForm={setDisplayForm}
                            displayForm={displayForm}
                            subImage={AnnouncementIcon}
                            handleSearchDebounce={handleSearchDebounce}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                        />
                       }
                        {
                            (loading || announcementsList === undefined) ? <Loader bottom='150px' />
                                :
                                (<div className={`${announcementsList?.allAnnouncements?.totalCount ? 'anouncementListContainer' : 'forNoDataFound'}`}>
                                    {
                                        announcementsList?.allAnnouncements?.totalCount ?
                                            announcementsList?.allAnnouncements?.nodes?.map((cardItem: announcementObjectType, index: number) => {
                                                return (
                                                    <div className='announcementCardContainer' key={`${index}-${cardItem?.aId}`} onClick={() => handleClickCard(cardItem)}>
                                                        <img className='announcementImg' src={cardItem?.aUrl ? baseUrl+cardItem?.aUrl : AnnouncementImage} alt='announcementImage'/>
                                                        {getScreenDimensions()?.isMobileScreen ? <>
                                                            <div className='right-container'>
                                                                <div className='topSectionTitle'>
                                                                    <Tooltip 
                                                                    title={cardItem?.aOriginalTitle}
                                                                    disableHoverListener={!isOverflow[index]}
                                                                    placement="top"
                                                                    >
                                                                        <label className='announcementTitleStyle' ref={(el) => (announcementTitleRefs.current[index] = el)} onMouseEnter={() => checkOverflow(index)}>
                                                                            {cardItem?.aOriginalTitle}
                                                                        </label>
                                                                    </Tooltip>
                                                                    {role === SCHOOL_ADMIN && <div className='action-btn' onClick={(event) => handleActions(event, cardItem)}>
                                                                        <img src={MoreIcon} alt="moreIcon" className='moreIconStyle' />
                                                                    </div>}
                                                                </div>
                                                                <div className='messageCtn'>
                                                                    {cardItem?.aMessage}
                                                                </div>
                                                            </div>
                                                        </> : <>
                                                            <div className='topSectionTitle'>
                                                                <Tooltip 
                                                                title={cardItem?.aOriginalTitle}
                                                                disableHoverListener={!isOverflow[index]}
                                                                placement="top"
                                                                >
                                                                    <label className='announcementTitleStyle' ref={(el) => (announcementTitleRefs.current[index] = el)} onMouseEnter={() => checkOverflow(index)}>
                                                                        {cardItem?.aOriginalTitle}
                                                                    </label>
                                                                </Tooltip>
                                                                {role === SCHOOL_ADMIN && <div className='action-btn' onClick={(event) => handleActions(event, cardItem)}>
                                                                    <img src={MoreIcon} alt="moreIcon" className='moreIconStyle' />
                                                                </div>}
                                                            </div>
                                                            <div className='messageCtn'>
                                                                {cardItem?.aMessage}
                                                            </div>
                                                        </>}
                                                    </div>
                                                )
                                            })
                                            :
                                            <div className='noDataFoundStyle'>No data found</div>
                                    }
                                </div>)
                        }

                        <Menu
                            id="action-menu"
                            MenuListProps={{
                                "aria-labelledby": "long-button",
                                disablePadding: true,
                            }}
                            anchorEl={anchorEl}
                            open={openOverlay}
                            onClose={handleCloseActions}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            sx={{
                                '& .MuiPaper-root': {
                                    borderRadius: '5px',
                                    boxShadow: '-10px 10px 15px 0.2px rgba(0, 0, 0, 0.05)',
                                },
                            }}
                        >
                            {actionData && <MaterialActionsOverlay
                                anchorEl={anchorEl}
                                open={openOverlay}
                                handleClose={handleCloseActions}
                                menuItems={menuItems}
                                actionsHeight={'112px'}
                                position="right"
                                verticalPosition = "bottom"
                               />}
                        </Menu>
                        <ConfirmationPopup
                            open={openConfirmationPopup}
                            message={action === 'delete' ?`Are you sure you want to Delete This Announcement?` : `Are you sure you want to share this announcement to all?`}
                            gifData={action === 'delete' ? DeleteGif : ShareGif}
                            yesButtonFunctionality={onYesButtonClick}
                            noButtonFunctionality={onNoButtonClick}
                            messageWidth={'195px'}
                        />
                    </div> 
            }
        </>
    )
}
export default AdminAnnouncement;
